import { h } from 'preact';
import './progress_indicator.scss';
import { BaseAnnotation } from '../base_annotation';
import { accessible } from 'utilities/accessibility';
import { LIFECYCLE_ORDER } from 'constants/annotations';
import { TIMELINE_SKIP_PERCENT } from 'configuration/defaults';
import { ANNOTATION_EVENT, APPLICATION_EVENT, PLAYER_EVENT, VIEW_PAGE } from 'constants/events';
import { getQuizAriaLabel, getTimeForAriaLabel } from './utilities';

export class ProgressIndicator extends BaseAnnotation {
    constructor(...args) {
        super(...args);
        this.state = {
            quizzes: [],
            isPresentModeActiveWithoutQuiz: null
        }
        this.eventBus.on(APPLICATION_EVENT.LEVEL_CHANGED, this.onLevelChanged);
        this.eventBus.on(ANNOTATION_EVENT.LIFECYCLE_CHANGE, this.onLifecyleChange);

        this.eventBus.onReplay(PLAYER_EVENT.AVAILABLE, (player, adapter) => {
            this.player = adapter;
            this.forceUpdate();
        });

        this.eventBus.on(PLAYER_EVENT.TIME_UPDATE, (player, adapter) => {
            const { furthestWatched, currentTime } = this; 
            this.furthestWatched = furthestWatched < currentTime ? currentTime : furthestWatched;
            this.forceUpdate();
        });

        this.eventBus.on(VIEW_PAGE.PRESENT_MODE_ACTIVE, (payload) => this.setIsVideoPresentModeWithOutQuizActive(payload))

        this.assignedIndicators = this.getAssignedIndicators();
        this.furthestWatched = 0;
    }

    componentDidMount() {
        const { descendants = [] } = this.props;

        if (descendants.length) {
            this.setState({
                quizzes: this.getQuizzes()
            })
        }
    }

    componentDidUpdate(...args) {
        super.componentDidUpdate(...args);
        this.assignedIndicators = this.getAssignedIndicators();
    }

    componentWillUnmount() {
        this.eventBus.off(APPLICATION_EVENT.LEVEL_CHANGED, this.onLevelChanged);
        this.eventBus.off(ANNOTATION_EVENT.LIFECYCLE_CHANGE, this.onLifecyleChange);
    }

    getAssignedIndicators() {
        return this.props.descendants?.map(d => d.id) || [];
    }

    onLevelChanged = level => {
        const { level: currentLevel } = this.props?.data?.state || {};

        if (currentLevel !== level) {
            const cleared = this.assignedIndicators.reduce((state, indicatorId) => ({
                ...state, [indicatorId]: null
            }), {});

            this.setState(cleared);
        }
    }

    onLifecyleChange = ({id, status }) => {
        if (!this.assignedIndicators.includes(id)) {
            return;
        }

        if (!this.state[id] || LIFECYCLE_ORDER.indexOf(status) > LIFECYCLE_ORDER.indexOf(this.state[id])) {
            this.setState({ [id]: status })
        }     
    }

    get classes() {
        const { classes } = this.props;

        return `progress-indicator vjs-control-bar ${super.classes}`;
    }

    getQuizzes = () => {
        const { descendants = [] } = this.props;
        return descendants.filter(descendant => descendant.type === 'quiz')
    }

    sortQuizzes = (quizzes) => {
        return quizzes.sort((a,b) => a.start - b.start);
    }

    getQuizIndexById = (id) => {
        const quizzes = this.sortQuizzes(this.state.quizzes);

        if (!quizzes.length) {
            return;
        }

        const currentQuiz = quizzes.find(quiz => quiz.id === id);
        const currentQuizIndex = quizzes.indexOf(currentQuiz);
        //okay to return undefined here, because if the descendant is not a quiz,
        //the value set in the map below will never be used. 
        return currentQuizIndex + 1; 
    }

    setIsVideoPresentModeWithOutQuizActive = (payload) => {
        this.setState({ isPresentModeActiveWithoutQuiz: payload.isPresentModeActiveWithoutQuiz })
    }

    renderProgressIndicators = (descendants, duration) => {
        if (this.state.isPresentModeActiveWithoutQuiz) {
            return null;
        }

        return this.sortQuizzes(descendants) .map(({ id, start, internal, type }) => {
            const quizIndex = this.getQuizIndexById(id);

            return (
                <Indicator
                    key={id}
                    time={start}
                    duration={duration}
                    status={this.state[id]}
                    name={internal?.name}
                    onClick={e => {
                            this.toTime(start);
                            e.stopPropagation();
                        }}
                    index={quizIndex}
                    type={type}
                />
            )
        })
    }

    render() {
        const { duration, currentTime, furthestWatched } = this;
        const { descendants = [] } = this.props;
        
        return (
            <div className={ this.classes } style={this.getElementStyles()}>
                <div onClick={this.onProgressClick} className="progress-click">
                    <Timeline
                        currentTime={currentTime}
                        duration={duration}
                        furthestWatched={furthestWatched}
                        toTime={this.toTime}
                    />
                    {this.renderProgressIndicators(descendants, duration)}
                </div>                
            </div>
        )
    }
    
    onProgressClick = ({currentTarget, clientX}) => {
        const { duration } = this;
        const { width, left } = currentTarget.getBoundingClientRect();
        const time = (clientX - left) / width * duration;
        this.toTime(time);
    }

    get duration() {
        return this.player?.duration || 0;
    }

    get currentTime() {
        return this.player?.currentTime || 0;
    }
}

export const Indicator = ({
    time,
    duration,
    onClick,
    name,
    status='inactive',
    type,
    index
}) => {
    const position = time / duration * 100;
    const style = {
        left: `calc(${position}% - 8px)` // 8px is half of dot width (see progress_indicator.scss)
    };

    const ariaLabel =
        type === 'quiz' ? getQuizAriaLabel(index, time) : `Jump to ${getTimeForAriaLabel(time)} in video`;

    const attrs = {
        name: name,
        style: style,
        'data-status': status,
        className: 'indicator',
        onClick: onClick,
        'aria-label': ariaLabel,
        ...accessible(onClick)
    };

    return (
        <div {...attrs}>
            <div className="dot"/>
        </div>
    );
};

export const Timeline = ({ currentTime, duration, furthestWatched = 0, toTime }) => {
    const visitedLength = furthestWatched / duration * 100;
    const visitedStyles = { width: `${visitedLength}%` };

    return (            
        <div className="timeline">
            <div className="timeline visited" style={visitedStyles} />
            <Playhead currentTime={currentTime} duration={duration} toTime={toTime} />
        </div>
    );
};

export const Playhead = ({ currentTime, duration, toTime }) => {
    const playheadPosition = currentTime / duration * 100;
    const playheadStyle = { left: `calc(${playheadPosition}% - 8px)` }; // 8px is half of playhead width (see progress_indicator.scss)

    const timeForAriaLabel = getTimeForAriaLabel(currentTime);
    const ariaLabel = `Current playback at ${timeForAriaLabel}`;

    const onKeyDown = (e) => {
        // enter (13) and space (32) - plays from current time
        if (e.keyCode === 13 || e.keyCode === 32) {
            e.preventDefault();
            toTime(currentTime);
        }
        // right arrow - skips forward
        if (e.keyCode === 39) {
            toTime(currentTime + duration * TIMELINE_SKIP_PERCENT);
        }
        // left arrow - skips back
        if (e.keyCode === 37) {
            toTime(currentTime - duration * TIMELINE_SKIP_PERCENT);
        }
    };

    return (
        <div
            aria-label={ariaLabel}
            className="playhead"
            onKeyDown={onKeyDown}
            role="button"
            style={playheadStyle}
            tabIndex="0"
        />
    );
};
